import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { refreshChangelog } from '../api/changelog';
import { getConfig } from '../api/config';
import {
  deletePreRelease,
  importSpec,
  listJiraReleases,
  listVersions,
  preRelease,
  publish,
} from '../api/releases';
import { queryKeys } from './keys';

export function useConfig() {
  return useQuery({
    queryKey: queryKeys.config(),
    queryFn: getConfig,
    // The config is baked at deploy time and getConfig() already memoises it.
    staleTime: Infinity,
  });
}

export function useVersions(apiId: string, options?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: queryKeys.versions(apiId),
    queryFn: () => listVersions(apiId),
    refetchInterval: options?.refetchInterval ?? false,
  });
}

export function useJiraReleases(apiId: string) {
  return useQuery({
    queryKey: queryKeys.jiraReleases(apiId),
    queryFn: () => listJiraReleases(apiId),
    // Jira fix versions change rarely; don't re-hit Jira on every admin visit.
    staleTime: 5 * 60 * 1_000,
  });
}

/**
 * Every release mutation ends by invalidating the versions list — that single
 * rule replaces the hand-rolled `loadVersions()` calls that each handler used
 * to have to remember.
 */
function useVersionsInvalidator(apiId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.versions(apiId) });
}

export function useImportSpec(apiId: string) {
  const invalidate = useVersionsInvalidator(apiId);
  return useMutation({
    mutationFn: ({ versionId, source }: { versionId: string; source: string }) =>
      importSpec(apiId, versionId, { source }),
    // The registrar runs asynchronously off the S3 upload, so the row does not
    // exist yet at this point; refresh now for the fast path and once more after
    // the registrar has had a moment.
    onSuccess: () => {
      void invalidate();
      window.setTimeout(() => void invalidate(), 3_000);
    },
  });
}

export function useLinkJiraRelease(apiId: string) {
  const invalidate = useVersionsInvalidator(apiId);
  return useMutation({
    mutationFn: ({ versionId, jiraReleaseId }: { versionId: string; jiraReleaseId: string }) =>
      preRelease(apiId, versionId, { jiraReleaseId }),
    onSuccess: () => void invalidate(),
  });
}

export function usePublishVersion(apiId: string) {
  const invalidate = useVersionsInvalidator(apiId);
  return useMutation({
    mutationFn: (versionId: string) => publish(apiId, versionId),
    onSuccess: () => void invalidate(),
  });
}

export function useDeletePreRelease(apiId: string) {
  const invalidate = useVersionsInvalidator(apiId);
  return useMutation({
    mutationFn: (versionId: string) => deletePreRelease(apiId, versionId),
    onSuccess: () => void invalidate(),
  });
}

export function useRefreshChangelog(apiId: string) {
  const invalidate = useVersionsInvalidator(apiId);
  return useMutation({
    mutationFn: (versionId: string) => refreshChangelog(apiId, versionId),
    onSuccess: () => void invalidate(),
  });
}
